<script lang="ts" setup>
import useUserService from '@/composables/apiServices/useUserService.js';
import useApiService from '@/composables/apiServices/useApiService';
import { changeLanguage, loadLocaleMessages, setI18nLanguage } from '@/composables/useChangeLocale';
import SuspendedErrorHandler from '@/components/suspense/SuspendedErrorHandler.vue';
import useAuthenticatedQuery from '@/composables/apiServices/useAuthenticatedQuery';
import { onBeforeUnmount, watch } from 'vue';
import useEventSource from '@/composables/apiServices/useEventSource';
import timezones from '@/assets/json/timezones.json';
import { useTheme } from 'vuetify';
import { provideTheme } from 'vuetify/lib/composables/theme.mjs';
import UpdatesHandler from '../service-updates/UpdatesHandler.vue';
import { enabledThemesNames, getThemeMode } from '@/helpers/theme';
import type { IDetailsUpdateRequest, SelectedColor } from '@/types/services/user.service';

const { healthCheck } = useApiService();
const { suspense: refreshXsrfSuspense } = healthCheck();
const theme = useTheme();

const { themeClasses } = provideTheme({});
console.info('Starting z-mail initialization');
// Preload the XSRF token
await refreshXsrfSuspense();

/**
 * Initialize the locale for the application
 */
const { queriesEnabled, userIndex } = useAuthenticatedQuery();
const { details, updateDetails } = useUserService();
const { suspense: loadUserDetails, data: userDetails } = details();
const { mutateAsync: updateUserDetails } = updateDetails();

const initLocale = async () => {
  let locale: string = navigator.language.slice(0, 2);
  const fallbackLocale = import.meta.env.VITE_I18N_FALLBACK_LOCALE ?? 'en';

  // Get locale value from DB
  if (!navigator.cookieEnabled) {
    console.debug('Using browser locale', locale);
  } else if (queriesEnabled.value) {
    console.debug('Loading user details to get locale');
    const { data: userData } = await loadUserDetails();
    if (userData?.language) {
      locale = userData.language;
      console.debug('Using user locale from DB', locale);
    } else {
      console.debug('No user locale set in DB, using browser locale', locale);
    }
  } else {
    console.debug('Queries not enabled, using browser locale', locale);
  }

  const localesToLoad = [locale];
  if (locale !== fallbackLocale) {
    localesToLoad.push(fallbackLocale);
  }

  console.debug('Loading locales', localesToLoad);
  const [mainResult] = await Promise.allSettled(localesToLoad.map(loadLocaleMessages));
  if (mainResult?.status === 'fulfilled') {
    return setI18nLanguage(locale);
  }
  setI18nLanguage(fallbackLocale).catch((err) => {
    console.warn('Error setting fallback locale', err);
  });
};

const syncUserDetails = async () => {
  if (!queriesEnabled.value) {
    return;
  }

  const { data: userDetails } = await loadUserDetails();
  if (!userDetails) {
    // This should never happen
    throw new Error('User details not found');
  }
  const updateRequest: Partial<IDetailsUpdateRequest> = {};
  if (!userDetails.meta.browserTimezoneSet) {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const localTz = timezones.find((TZ) => TZ.value === tz || TZ.utc.includes(tz))?.value;
    updateRequest.timezone = localTz;
  }

  if (!enabledThemesNames.includes(userDetails.meta.ui.color)) {
    updateRequest.ui = {
      ...userDetails.meta.ui,
      color: (import.meta.env.VITE_DEFAULT_THEME ??
        enabledThemesNames[0] ??
        'blue') as SelectedColor
    };
  }

  if (Object.keys(updateRequest).length) {
    // Only update if there are changes to be made
    await updateUserDetails(updateRequest);
  }
};

const maybeResetUpgradeRequest = async () => {
  if (!queriesEnabled.value) {
    return;
  }

  const { data: userDetails } = await loadUserDetails();
  if (
    !!userDetails?.meta.upgrade_request &&
    userDetails.meta.upgrade_request.current_uname !== userDetails.meta.uname
  ) {
    await updateUserDetails({
      upgrade_request: null
    });
  }
};
const init = async () => {
  await Promise.all([
    // Add all async functions that need initialization for the whole application here
    initLocale(),
    syncUserDetails()
  ]);
  // This does not need to be awaited
  maybeResetUpgradeRequest();
};

// Actually initialize the application
await init();
const { close } = useEventSource();
console.info('Finished z-mail initialization');
console.groupEnd();

watch(userIndex, async () => {
  console.info('User index changed. Re-initializing.');
  await init();
});

watch(
  () => userDetails.value?.language,
  async (newLang) => {
    if (!newLang) {
      return;
    }
    console.info('User language changed. Re-initializing locale.');
    changeLanguage(newLang);
  }
);

// Watch for changes in the settings form and update the theme accordingly
// This is necessary to ensure the theme updates when the user changes settings on other tabs
watch(
  [() => userDetails.value?.meta.ui.theme, () => userDetails.value?.meta.ui.color],
  () => {
    theme.change(
      `${userDetails.value?.meta.ui.color ?? import.meta.env.VITE_DEFAULT_THEME ?? enabledThemesNames[0] ?? 'blue'}${
        getThemeMode(userDetails.value?.meta.ui) === 'dark' ? '-dark' : ''
      }`
    );
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  close();
});
</script>

<template>
  <v-layout
    :class="[
      themeClasses,
      {
        'content--dark': theme.global.name.value.includes('dark'),
        'content--light': !theme.global.name.value.includes('dark')
      }
    ]"
    class="w-100 application"
  >
    <SuspendedErrorHandler>
      <slot></slot>
    </SuspendedErrorHandler>
    <UpdatesHandler />
  </v-layout>
</template>

<style lang="scss" scoped>
.application {
  color: rgb(var(--v-theme-alert-close));
  background-color: rgb(var(--v-theme-background));
}
</style>
